// ─── Copy Trade Execution Bus ────────────────────────────────
// Global pub/sub for cTrader execution events, decoupled from
// React hooks. Multiple engines (copy trade, bot runner, etc.)
// can subscribe to the same execution stream.

export interface CopyExecutionEvent {
  type: 'execution';
  accountId: number;
  payload: Record<string, unknown>;
  timestamp: number;
}

type ExecutionListener = (event: CopyExecutionEvent) => void;

class CopyTradeExecutionBusImpl {
  private listeners: Set<ExecutionListener> = new Set();
  private lastEvents: CopyExecutionEvent[] = [];
  private maxEvents = 100;

  /** Subscribe to execution events. Returns unsubscribe function. */
  subscribe(fn: ExecutionListener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /** Publish an execution event to all listeners. */
  publish(event: CopyExecutionEvent): void {
    this.lastEvents.push(event);
    if (this.lastEvents.length > this.maxEvents) {
      this.lastEvents = this.lastEvents.slice(-this.maxEvents);
    }

    this.listeners.forEach((fn) => {
      try {
        fn(event);
      } catch {
        /* don't let one bad listener break others */
      }
    });
  }

  /** Get recent execution events (for late subscribers / debugging). */
  getRecentEvents(limit = 20): CopyExecutionEvent[] {
    return this.lastEvents.slice(-limit);
  }

  /** Number of active listeners. */
  get listenerCount(): number {
    return this.listeners.size;
  }
}

export const copyTradeExecutionBus = new CopyTradeExecutionBusImpl();